import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
	Calculator,
	Play,
	Loader2,
	TrendingUp,
	Info,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/utils/cn";
import { testScoring } from "@/services/simulation";
import { useToast } from "@/components/ui/use-toast";

const commonApps = [
	"Visual Studio Code",
	"Google Chrome",
	"Slack",
	"Zoom",
	"Microsoft Teams",
	"Figma",
	"Terminal",
	"Postman",
	"Discord",
	"Spotify",
];

export function ScoringCalculator() {
	const { toast } = useToast();
	const [formData, setFormData] = useState({
		processName: "Visual Studio Code",
		windowTitle: "index.tsx - project - Visual Studio Code",
		coveragePercent: 75,
		zOrder: 0,
		isActive: true,
		sessionDuration: 30,
		switchCount: 5,
		breaksToday: 2,
		consecutiveHours: 3,
	});

	const scoringMutation = useMutation({
		mutationFn: testScoring,
		onError: (error: any) => {
			toast({
				title: "Error",
				description: error.message || "Failed to calculate score",
				variant: "destructive",
			});
		},
	});

	const handleSubmit = () => {
		scoringMutation.mutate({
			processName: formData.processName,
			windowTitle: formData.windowTitle,
			coveragePercent: formData.coveragePercent,
			zOrder: formData.zOrder,
			isActive: formData.isActive,
			sessionDuration: formData.sessionDuration * 60000, // Convert to ms
			switchCount: formData.switchCount,
			breaksToday: formData.breaksToday,
			consecutiveHours: formData.consecutiveHours,
		});
	};

	const result = scoringMutation.data;

	const getScoreColor = (score: number) => {
		if (score >= 85) return "text-green-600";
		if (score >= 70) return "text-blue-600";
		if (score >= 50) return "text-amber-600";
		if (score >= 30) return "text-orange-600";
		return "text-gray-600";
	};

	const getScoreBgColor = (score: number) => {
		if (score >= 85) return "bg-green-100";
		if (score >= 70) return "bg-blue-100";
		if (score >= 50) return "bg-amber-100";
		if (score >= 30) return "bg-orange-100";
		return "bg-gray-100";
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
						<Calculator className="w-6 h-6" />
						Productivity Scoring Calculator
					</h1>
					<p className="text-gray-500 mt-1">
						Test the scoring engine with custom inputs
					</p>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Input Form */}
				<Card>
					<CardHeader>
						<CardTitle>Input Parameters</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						{/* App Selection */}
						<div>
							<Label>Application</Label>
							<div className="flex flex-wrap gap-2 mt-2">
								{commonApps.map((app) => (
									<Button
										key={app}
										size="sm"
										variant={formData.processName === app ? "default" : "outline"}
										onClick={() => setFormData({ ...formData, processName: app })}
									>
										{app}
									</Button>
								))}
							</div>
							<Input
								className="mt-2"
								placeholder="Or type custom app name..."
								value={formData.processName}
								onChange={(e) =>
									setFormData({ ...formData, processName: e.target.value })
								}
							/>
						</div>

						{/* Window Title */}
						<div>
							<Label>Window Title</Label>
							<Input
								className="mt-2"
								placeholder="Enter window title..."
								value={formData.windowTitle}
								onChange={(e) =>
									setFormData({ ...formData, windowTitle: e.target.value })
								}
							/>
						</div>

						{/* Coverage Slider */}
						<div>
							<div className="flex items-center justify-between mb-2">
								<Label>Screen Coverage</Label>
								<Badge variant="outline">{formData.coveragePercent}%</Badge>
							</div>
							<Slider
								value={[formData.coveragePercent]}
								onValueChange={(values: number[]) =>
									setFormData({ ...formData, coveragePercent: values[0] })
								}
								max={100}
								step={5}
							/>
						</div>

						{/* Z-Order */}
						<div>
							<div className="flex items-center justify-between mb-2">
								<Label>Z-Order (Window Position)</Label>
								<Badge variant="outline">{formData.zOrder === 0 ? "Foreground" : `Background (${formData.zOrder})`}</Badge>
							</div>
							<Slider
								value={[formData.zOrder]}
								onValueChange={(values: number[]) =>
									setFormData({ ...formData, zOrder: values[0] })
								}
								max={5}
								step={1}
							/>
						</div>

						{/* Is Active Toggle */}
						<div className="flex items-center justify-between">
							<div>
								<Label>Is Active Window</Label>
								<p className="text-sm text-gray-500">Currently being used</p>
							</div>
							<Switch
								checked={formData.isActive}
								onCheckedChange={(checked) =>
									setFormData({ ...formData, isActive: checked })
								}
							/>
						</div>

						{/* Advanced Options */}
						<div className="border-t pt-4">
							<h4 className="font-medium text-gray-700 mb-4">Advanced (optional)</h4>
							<div className="grid grid-cols-2 gap-4">
								<div>
									<Label>Session Duration (min)</Label>
									<Input
										type="number"
										value={formData.sessionDuration}
										onChange={(e) =>
											setFormData({
												...formData,
												sessionDuration: Number(e.target.value),
											})
										}
									/>
								</div>
								<div>
									<Label>Switch Count (10 min)</Label>
									<Input
										type="number"
										value={formData.switchCount}
										onChange={(e) =>
											setFormData({
												...formData,
												switchCount: Number(e.target.value),
											})
										}
									/>
								</div>
								<div>
									<Label>Breaks Today</Label>
									<Input
										type="number"
										value={formData.breaksToday}
										onChange={(e) =>
											setFormData({
												...formData,
												breaksToday: Number(e.target.value),
											})
										}
									/>
								</div>
								<div>
									<Label>Consecutive Hours</Label>
									<Input
										type="number"
										value={formData.consecutiveHours}
										onChange={(e) =>
											setFormData({
												...formData,
												consecutiveHours: Number(e.target.value),
											})
										}
									/>
								</div>
							</div>
						</div>

						{/* Calculate Button */}
						<Button
							className="w-full"
							size="lg"
							onClick={handleSubmit}
							disabled={scoringMutation.isPending}
						>
							{scoringMutation.isPending ? (
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
							) : (
								<Play className="w-4 h-4 mr-2" />
							)}
							Calculate Score
						</Button>
					</CardContent>
				</Card>

				{/* Results */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<TrendingUp className="w-5 h-5" />
							Result
						</CardTitle>
					</CardHeader>
					<CardContent>
						{result ? (
							<div className="space-y-6">
								{/* Final Score */}
								<div className={cn("text-center p-6 rounded-lg", getScoreBgColor(result.finalScore))}>
									<p className="text-sm text-gray-600 mb-2">Final Score</p>
									<p className={cn("text-6xl font-bold", getScoreColor(result.finalScore))}>
										{result.finalScore}
									</p>
									<Badge className="mt-2">{result.label}</Badge>
									<p className="text-sm mt-2 text-gray-600">{result.message}</p>
								</div>

								{/* Score Breakdown */}
								<div>
									<h4 className="font-medium text-gray-700 mb-3">Breakdown</h4>
									<div className="space-y-3">
										{Object.entries(result.breakdown).map(([key, data]: [string, any]) => (
											<div key={key}>
												<div className="flex items-center justify-between text-sm mb-1">
													<span className="capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
													<span className="font-medium">
														{Math.round(data.value)}/100 ({Math.round(data.weight * 100)}%)
													</span>
												</div>
												<div className="w-full bg-gray-200 rounded-full h-2">
													<div
														className="h-2 rounded-full bg-blue-500"
														style={{ width: `${data.value}%` }}
													/>
												</div>
											</div>
										))}
									</div>
								</div>

								{/* Recommendations */}
								{result.recommendations && result.recommendations.length > 0 && (
									<div>
										<h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
											<Info className="w-4 h-4" />
											Recommendations
										</h4>
										<ul className="space-y-2">
											{result.recommendations.map((rec: string, index: number) => (
												<li key={index} className="text-sm text-gray-600 flex items-start gap-2">
													<span className="text-blue-500">•</span>
													{rec}
												</li>
											))}
										</ul>
									</div>
								)}
							</div>
						) : (
							<div className="text-center py-12 text-gray-500">
								<Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
								<p>Adjust the inputs and click Calculate to see results</p>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
